<template>
  <div class="form-label">{{ $t('campaigns.google_ads.logo_image') }}</div>
  <div class="images-grid">
    <AdImagePicker
      :imageUrl="logoImageUrl"
      :uploading="isUploading"
      boxSize
      @click="triggerFileInput"
      @remove="() => (companyLogos = [])" />
  </div>

  <div v-if="props.errors">
    <FormErrorList :errors="props.errors" />
  </div>

  <input type="file" ref="fileInput" accept=".jpg,.jpeg,.png,.gif,.webp" class="d-none" @change="onFileSelected" />
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { FormErrorList } from '@prospective/lithium';
import { hostname, requestMultipart } from '@/api/client';
import AdImagePicker from './AdImagePicker.vue';
import type { ImageObject } from '@/types/types';

const props = defineProps<{
  companyId: number;
  errors?: string[];
}>();

const companyLogos = defineModel<ImageObject[]>({ required: true, default: () => [] });

const fileInput = ref<HTMLInputElement | null>(null);
const isUploading = ref(false);

const triggerFileInput = () => {
  if (isUploading.value) return;
  fileInput.value?.click();
};

const logoImageUrl = computed<string | null>(() => {
  const logo = companyLogos.value?.[0];
  if (!logo?.id) return null;

  const thumbnail = logo.proxy?.url_thumbnail;
  if (thumbnail) {
    try {
      new URL(thumbnail);
      return thumbnail;
    } catch {}
  }

  try {
    const url = new URL(`/v1/media/images/${logo.id}/jump`, hostname);
    return url.toString();
  } catch {
    return null;
  }
});

const onFileSelected = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (!target.files?.length) return;

  const file = target.files[0];
  const formData = new FormData();
  formData.append('image', file);

  isUploading.value = true;
  try {
    const res = await requestMultipart('POST', '/v1/account/media/images/upload', { class: 0 }, formData);
    companyLogos.value = [res.data as unknown as ImageObject];
  } catch (error) {
    console.error('Failed to upload image:', error);
  } finally {
    isUploading.value = false;
    if (fileInput.value) {
      fileInput.value.value = '';
    }
  }
};
</script>

<style scoped lang="scss">
.form-label {
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 6px;
  color: #344054;
}

.images-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: flex-start;
}
</style>
